import React from 'react';
import clsx from 'clsx';

/** The dot and, when the chip is the one in use, the fill. Same tones as `ToneBadge`. */
const TONES = {
  success: { dot: 'bg-ninja-green', active: 'bg-ninja-green border-ninja-green text-white' },
  warning: { dot: 'bg-warning', active: 'bg-warning border-warning text-white' },
  info: { dot: 'bg-info', active: 'bg-info border-info text-white' },
  danger: { dot: 'bg-danger', active: 'bg-danger border-danger text-white' },
  neutral: { dot: 'bg-text-muted', active: 'bg-ninja-navy border-ninja-navy text-white' },
};

const IDLE =
  'bg-white dark:bg-[#1a1a1a] text-text-secondary border-border hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]';

const Chip = ({ isActive, tone, count, testId, value, onClick, children }) => {
  const style = TONES[tone] ?? TONES.neutral;
  return (
    <button
      type="button"
      aria-pressed={isActive}
      data-testid={testId}
      data-value={value}
      onClick={onClick}
      className={clsx(
        'px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 border',
        isActive ? [style.active, 'shadow-sm'] : IDLE
      )}
    >
      <span className={clsx('w-2.5 h-2.5 rounded-full flex-shrink-0', isActive ? 'bg-white' : style.dot)} />
      {children}
      {/* No number is not the same as zero: a count the server did not send is left out. */}
      {count !== undefined && count !== null && <span className="tabular-nums">· {count}</span>}
    </button>
  );
};

/**
 * A filter by status as pills, each with how many there are.
 *
 * The generic version of what `/deliveries` does with its own catalog: here the
 * statuses, their labels and their tones are handed in. They wrap instead of
 * scrolling, so none is cut off at the edge with nothing saying it exists.
 *
 * @param {object} props
 * @param {Array<{value: string, label: string, count?: number|string|null,
 *   tone?: 'success'|'warning'|'info'|'danger'|'neutral'}>} props.options
 * @param {string} props.value - The status in use, or `allValue`.
 * @param {(value: string) => void} props.onChange
 * @param {string} [props.allValue='all']
 * @param {string} [props.allLabel='Todas']
 * @param {number|null} [props.total] - Shown on the "all" chip.
 * @param {boolean} [props.showAll=true] - Without the "all" chip the options
 *   are tabs rather than a filter: one of them is always in use, and pressing
 *   it again leaves it there.
 * @param {string} [props.testId='count-chips'] - Each chip is `${testId}-chip`,
 *   told apart by its `data-value`.
 */
export const CountChips = ({
  options,
  value,
  onChange,
  allValue = 'all',
  allLabel = 'Todas',
  total,
  showAll = true,
  testId = 'count-chips',
}) => (
  <div data-testid={testId} className="flex flex-wrap items-center gap-2.5">
    {showAll && (
      <Chip
        isActive={value === allValue}
        tone="success"
        count={total}
        testId={`${testId}-chip`}
        value={allValue}
        onClick={() => onChange(allValue)}
      >
        {allLabel}
      </Chip>
    )}

    {options.map((option) => (
      <Chip
        key={option.value}
        isActive={value === option.value}
        tone={option.tone}
        count={option.count}
        testId={`${testId}-chip`}
        value={option.value}
        // Pressing the one in use goes back to all of them, when there is an "all".
        onClick={() => onChange(showAll && value === option.value ? allValue : option.value)}
      >
        {option.label}
      </Chip>
    ))}
  </div>
);
